import { takeEvery, take, call, put, select, all } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import { PREMIUM_CAP_RULE } from 'constants/logic';
import { makePath } from 'helpers/link-generation';
import { premiumCaps, venueClasses, classDetail } from 'store/saga/entities/bound';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { openModal, closeModal, waitForModal, CLOSE_MODAL } from 'components/Modal';
import { selectActiveVenueId } from 'selectors/general';
import { ACTIVE_VENUE_CHANGED } from 'features/App/App.saga';
import { reportError } from 'helpers/error-logging';
import { sagaMessages as messages } from './PremiumCaps.messages';
import { selectPremiumCapsRule } from './PremiumCaps.selectors';

const RULE_ERROR_TYPE = {
  CONFLICT: 'CONFLICT',
};

export const OPEN_PREMIUM_CAP_RULE_MODAL = 'classpass_vp/premium_caps/OPEN_PREMIUM_CAP_RULE_MODAL';
export const FETCH_SCHEDULES_FOR_CLASS = 'classpass_vp/premium_caps/FETCH_SCHEDULES_FOR_CLASS';
export const PREMIUM_CAP_MODAL_RESPONSE = 'classpass_vp/premium_caps/PREMIUM_CAP_MODAL_RESPONSE';
export const DELETE_PREMIUM_CAP_RULE = 'classpass_vp/premium_caps/DELETE_PREMIUM_CAP_RULE';

export const addPremiumCapRule = venueId => ({ type: OPEN_PREMIUM_CAP_RULE_MODAL, venueId });
export const editPremiumCapRule = (venueId, rule) => ({ type: OPEN_PREMIUM_CAP_RULE_MODAL, venueId, rule });
export const fetchSchedulesForClass = (parseDaysOfWeek, setDaysOfWeek, classId) => ({
  type: FETCH_SCHEDULES_FOR_CLASS,
  classId,
  parseDaysOfWeek,
  setDaysOfWeek,
});
export const premiumCapModalResponse = (data, { resolveSubmit, rejectSubmit }) => ({
  type: PREMIUM_CAP_MODAL_RESPONSE,
  data,
  resolveSubmit,
  rejectSubmit,
});
export const deletePremiumCapRule = (venueId, ruleId) => ({ type: DELETE_PREMIUM_CAP_RULE, venueId, ruleId });

export function* handleRouteEnter(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntity, premiumCaps, [client, venueId]);
  } catch (error) {
    yield put(createErrorBanner(messages.error.loadingRules));
  }
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  yield call(client.navigate, makePath.premiumCaps(venueId));
}

function getConflictMessage(conflictingRule) {
  const { ruleType, spotCap, ruleEntityId, entityMetadata = {}, displayData = {} } = conflictingRule;

  if (ruleType === PREMIUM_CAP_RULE.VENUE_DEFAULT) {
    return {
      ...messages.conflict.venueDefault,
      values: { spotCap },
    };
  }

  // an accurate message would require ridiculous enumeration...
  //  dateStart only, dateStart|End only, daysOfWeek only, timeOfDayStart|End only
  //  and every possible conbination
  // ... no thanks!
  if (ruleType === PREMIUM_CAP_RULE.VENUE) {
    return {
      ...messages.conflict.venue,
      values: { spotCap },
    };
  }

  if (ruleType === PREMIUM_CAP_RULE.CLASS) {
    return {
      ...messages.conflict.class,
      values: {
        id: ruleEntityId,
        spotCap,
        ...entityMetadata,
      },
    };
  }

  // like PREMIUM_CAP_RULE.VENUE, enumeration is icky... skip!
  if (ruleType === PREMIUM_CAP_RULE.RECURRING_SCHEDULE) {
    return {
      ...messages.conflict.recurringSchedule,
      values: {
        spotCap,
        ...entityMetadata,
      },
    };
  }

  if (ruleType === PREMIUM_CAP_RULE.SCHEDULE) {
    return {
      ...messages.conflict.schedule,
      values: {
        id: ruleEntityId,
        spotCap,
        ...entityMetadata,
        ...displayData,
      },
    };
  }

  // No one should ever see this message
  return messages.conflict.generic;
}

export function* doOpenPremiumCapRuleModal(client, action) {
  const { venueId, rule } = action;

  try {
    if (!rule) {
      yield put(openModal('AddPremiumCapsRuleModal'));
    } else {
      yield put(openModal('EditPremiumCapsRuleModal', { rule }));
    }
    yield call(fetchEntity, venueClasses, [client, { venueId }]);
  } catch (error) {
    yield put(createErrorBanner(messages.error.loadingClasses));
  }

  let resolveSubmit;
  let rejectSubmit;

  // This loop lets us send rules to the backend for validation, show any errors
  // to the user, and let them make changes.
  while (true) {
    try {
      const modalResponse = yield take([PREMIUM_CAP_MODAL_RESPONSE, CLOSE_MODAL]);
      const { data } = modalResponse;
      ({ resolveSubmit, rejectSubmit } = modalResponse);
      if (!data) {
        break;
      }
      yield call(fetchEntity, premiumCaps, [client, venueId, data]);
      yield all([call(resolveSubmit), put(createInfoBanner(messages.success.ruleSaved))]);
      break;
    } catch (error) {
      const { reason, conflictingRuleIds } = error;

      if (!reason) {
        yield all([call(rejectSubmit), put(createErrorBanner(messages.error.savingRule))]);
      } else if (reason !== RULE_ERROR_TYPE.CONFLICT) {
        // TODO: currently shows generic message; do we want to handle all cases?
        yield all([call(rejectSubmit), put(createErrorBanner(messages.error.generic))]);
      } else if (reason === RULE_ERROR_TYPE.CONFLICT && conflictingRuleIds.length) {
        const conflictingRule = yield select(selectPremiumCapsRule, venueId, conflictingRuleIds[0]);
        const conflictMessage = getConflictMessage(conflictingRule);
        yield all([call(rejectSubmit, conflictMessage), put(createErrorBanner(messages.error.ruleConflict))]);
      }
    }
  }

  yield put(closeModal());
}

export function* doFetchSchedulesForClass(client, action) {
  const { classId, parseDaysOfWeek, setDaysOfWeek } = action;

  try {
    const venueId = yield select(selectActiveVenueId);
    const { futureSchedules } = yield call(fetchEntity, classDetail, [client, venueId, classId]);
    yield call(setDaysOfWeek, parseDaysOfWeek(futureSchedules));
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.loadingSchedules, { classId })), call(reportError, error)]);
  }
}

export function* doDeletePremiumCapRule(client, action) {
  const { venueId, ruleId } = action;

  try {
    yield put(closeModal()); // close any open modals so no click-events linger
    const confirmed = yield call(waitForModal, client, 'DeletePremiumCapsRuleModal');
    if (confirmed) {
      yield call(fetchEntity, premiumCaps, [client, venueId, { id: ruleId }]);
      yield put(createInfoBanner(messages.success.deleteRule));
    }
  } catch (error) {
    yield put(createErrorBanner(messages.error.deleteRule));
  }
}

export function* watchActions(client) {
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
  yield takeEvery(OPEN_PREMIUM_CAP_RULE_MODAL, doOpenPremiumCapRuleModal, client);
  yield takeEvery(FETCH_SCHEDULES_FOR_CLASS, doFetchSchedulesForClass, client);
  yield takeEvery(DELETE_PREMIUM_CAP_RULE, doDeletePremiumCapRule, client);
}
